import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';

interface StepWelcomeProps {
  onNext: (name: string) => void;
  onSkip: () => void;
}

export const StepWelcome = ({ onNext, onSkip }: StepWelcomeProps) => {
  const { t } = useLanguage();
  const [name, setName] = useState('');

  // Prefill name from auth metadata if available
  useEffect(() => {
    const loadUserName = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.user_metadata?.full_name) {
        setName(user.user_metadata.full_name);
      } else if (user?.email) {
        // Extract name from email prefix
        const emailPrefix = user.email.split('@')[0];
        const formattedName = emailPrefix
          .split(/[._-]/)
          .map(part => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ');
        setName(formattedName);
      }
    };
    loadUserName();
  }, []);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="space-y-2">
        <p className="text-sm text-muted-foreground text-center">
          {t('onboarding.welcome.tagline').replace('\n', ' ')}
        </p>
        <h2 className="text-2xl font-bold text-center">
          {t('onboarding.welcome.greeting')}
        </h2>
        <p className="text-xs text-muted-foreground text-center">
          {t('onboarding.changeLater')}
        </p>
      </div>

      <div className="space-y-3 pt-2">
        <Input
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('onboarding.welcome.namePlaceholder')}
          className="text-lg min-h-touch"
          autoFocus
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim()) {
              onNext(name.trim());
            }
          }}
        />
      </div>

      <div className="space-y-2 pt-2">
        <Button
          onClick={() => onNext(name.trim() || 'User')}
          className="w-full min-h-touch"
          disabled={!name.trim()}
        >
          {t('onboarding.welcome.next')}
        </Button>
        <button 
          onClick={onSkip}
          className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors py-2"
        >
          {t('onboarding.welcome.skip')}
        </button>
      </div>
    </div>
  );
};
